import { useEffect } from 'react';
import { useUser } from 'auth/index';
import { getSignupMethod, trackSignupCompleted } from 'src/utils/signupAnalytics';
import { hasTrackedSignup, markTrackedSignup } from 'src/utils/signupTracking';

/**
 * A freshly created account is one whose Clerk user was created within this
 * window. Without the window every pre-existing user would fire the event on
 * their first visit after deploy, which would make the baseline fiction.
 */
const SIGNUP_WINDOW_MS = 15 * 60 * 1000;

/**
 * Fires `Signup Completed` once per new account.
 *
 * Mounted app-wide rather than on the post-signup route so it covers every
 * onboarding branch (create org, invite acceptance, domain auto-join) and does
 * not break if Clerk's redirect URLs change.
 */
export const useTrackSignupCompleted = () => {
  const { isSignedIn, user } = useUser();

  useEffect(() => {
    if (!isSignedIn || !user?.createdAt) return;

    // not a fresh account. a negative age (client clock skew) still counts as fresh
    if (Date.now() - user.createdAt.getTime() >= SIGNUP_WINDOW_MS) return;

    if (hasTrackedSignup(user.id)) return;

    // mark before sending: effects are double-invoked under StrictMode
    markTrackedSignup(user.id);
    trackSignupCompleted(getSignupMethod(user));
  }, [isSignedIn, user]);
};
